/**
 * BuildProgressBoard — Dashboard Build Engine P3 ingest slice (2026-07-28).
 *
 * Shows the REAL status of the tenant's recent knowledge-ingest jobs, so the
 * Business OS never presents a fake "done" (design §10: "No silent build
 * failures. The Progress board must show real job status, never a fake done.").
 *
 * Source of truth = GET /api/knowledge/ingest/recent (knowledgeApi
 * .fetchRecentIngestRuns). It refetches (a) on mount, (b) whenever a confirmed
 * ingest updates knowledgeAgentStore.lastResult — so a build the user just
 * confirmed in the banner appears here without a manual refresh — and (c) on the
 * explicit Refresh button. Renders nothing until there is real ingest activity.
 *
 * Design doc §8 (DashboardShell — progress board) + §10 (failure modes):
 * docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
 */
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { fetchRecentIngestRuns, type IngestResponse } from '@/services/knowledgeApi';
import { useKnowledgeAgentStore } from '@/stores/knowledgeAgentStore';

const STATUS_CLASS: Record<IngestResponse['overall_status'], string> = {
  completed: 'text-emerald-400',
  partial: 'text-amber-400',
  failed: 'text-red-400',
  processing: 'text-sky-400',
};

function perServiceLine(run: IngestResponse): string {
  return run.per_service
    .map((s) => (s.status === 'completed' ? `${s.service} ${s.chunks_created}` : `${s.service} ${s.status}`))
    .join(' · ');
}

export function BuildProgressBoard() {
  const [runs, setRuns] = useState<IngestResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Refetch when a confirmed ingest lands (banner Confirm / auto-approve sets it).
  const lastResult = useKnowledgeAgentStore((s) => s.lastResult);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchRecentIngestRuns(10);
      setRuns(resp.jobs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ingest status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, lastResult]);

  // Nothing to show until there is real activity (or an error to surface).
  if (!runs.length && !error) return null;

  return (
    <div
      data-testid="build-progress-board"
      className="w-full max-w-2xl rounded-lg border border-vibe-border bg-vibe-bg-secondary p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-vibe-text">What we've brought in</h3>
        <button
          type="button"
          data-testid="build-progress-refresh"
          onClick={() => void refresh()}
          disabled={loading}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-vibe-text-secondary hover:text-vibe-text disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <p data-testid="build-progress-error" className="text-sm text-red-400">
          {error}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {runs.map((run) => (
          <li
            key={run.job_id}
            data-testid={`build-progress-run-${run.job_id}`}
            className="flex items-center justify-between gap-3 rounded bg-vibe-bg px-3 py-2 text-sm"
          >
            <span className="min-w-0 flex-1 truncate text-vibe-text" title={run.source_name}>
              {run.source_name}
            </span>
            <span className="shrink-0 text-xs text-vibe-text-secondary">{perServiceLine(run)}</span>
            <span className={`shrink-0 text-xs font-medium ${STATUS_CLASS[run.overall_status]}`}>
              {run.overall_status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default BuildProgressBoard;
